import React, { useEffect, useState } from 'react';
import { Modal, Button, Table, Badge, Card } from 'react-bootstrap';
import { FaCheckCircle, FaArrowRight, FaBoxOpen, FaMoneyBillWave, FaBarcode, FaPrint } from 'react-icons/fa';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { getAllItemsWithStock } from '../../redux/features/stock.features';

import PrintableLabels from '../barcode/PrintableLabels';
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { formatCurrency } from '../../utility';
import { sendWhatsApp, WhatsAppFormatter } from '../../utility/WhatsAppFormatter';
import apiService from '../../services/apiServices';
import { FaWhatsapp } from 'react-icons/fa';

const StockUpdateSuccessModal = ({ show, onHide, purchase, verifiedItems, onMakePayment }) => {
    const dispatch = useDispatch();
    const { stocks } = useSelector(state => state.stockReducer);
    const { companies } = useSelector(state => state.companyReducer);
    const [summaryItems, setSummaryItems] = useState([]);
    const [showLabelModal, setShowLabelModal] = useState(false);
    const [verifiedItemsForLabels, setVerifiedItemsForLabels] = useState([]);

    useEffect(() => {
        if (show) {
            // Refetch stock to ensure we have the very latest values
            dispatch(getAllItemsWithStock());
        }
    }, [show, dispatch]);

    useEffect(() => {
        if (purchase && stocks && stocks.length > 0) {
            const itemsToProcess = purchase.purchasedItems || purchase.items || [];
            if (itemsToProcess.length === 0) return;

            const calculated = itemsToProcess.map(pItem => {
                const itemId = pItem.item_id?._id || pItem.item_id;
                const stockItem = stocks.find(s => s._id?.toString() === itemId?.toString());
                const currentStock = stockItem?.stock_quantity || 0;

                const verifiedData = (verifiedItems || []).find(v => v.item_id?._id?.toString() === itemId?.toString() || v.item_id?.toString() === itemId?.toString());
                const verifiedQty = verifiedData ? verifiedData.verifiedQty : (pItem.verifiedQty || pItem.purchaseQty || 0);

                // --- ROBUST NAME RESOLUTION ---
                // We prefer variant-specific names, then snapshots, then fallbacks.
                const resolveItemName = () => {
                    // 1. Try variant specifics first
                    const vNameFromData = verifiedData?.variantName || pItem?.variantName ||
                        verifiedData?.variant_id?.variantName || pItem?.variant_id?.variantName;
                    if (vNameFromData) return vNameFromData;

                    // 2. Check if we have variant attributes to construct a name
                    const attributes = verifiedData?.variantAttributes || pItem?.variantAttributes || [];
                    const attrString = attributes.length > 0
                        ? ` (${attributes.map(a => a.value).filter(Boolean).join(' / ')})`
                        : '';

                    // 3. Fallback to Snapshot Names (These are usually authoritative for the transaction)
                    const snapshotName = pItem?.itemName || verifiedData?.itemName;
                    if (snapshotName) return `${snapshotName}${attrString}`;

                    // 4. Final Fallbacks (Populated objects or Redux)
                    return pItem?.item_id?.itemName || verifiedData?.item_id?.itemName || stockItem?.itemName || "Unknown Item";
                };

                const vName = resolveItemName();
                console.log("Resolved vName:", vName, { pItem, verifiedData });
                // Hybrid Fix: Handle stale data lag
                // If currentStock from DB is less than what we just added, it is definitely stale.
                // In that case, we add the verifiedQty to project the new level.
                // Otherwise, we assume it's fresh (or at least consistent enough).
                let effectiveNewStock = currentStock;
                if (currentStock < verifiedQty) {
                    effectiveNewStock = currentStock + verifiedQty;
                }

                const oldStock = Math.max(0, effectiveNewStock - verifiedQty);

                return {
                    name: vName,
                    oldStock,
                    added: verifiedQty,
                    newStock: oldStock + verifiedQty, // Ensure math consistency (Prevent 0 + 1 = 0)
                    unit: stockItem?.unit?.name || 'Units'
                };
            });
            setSummaryItems(calculated);
        }
    }, [purchase, stocks]);

    const handlePrepareLabels = async () => {
        const purchaseId = purchase?._id || purchase?.purchase_id;
        if (!purchaseId) {
            return toast.warning("No purchase ID found for barcode generation.");
        }

        try {
            toast.info("Fetching authoritative barcode data...");
            const res = await apiService.get(`/purchase/barcode-data/${purchaseId}`);
            const labels = res.data;

            if (!labels || labels.length === 0) {
                return toast.warning("No items found to print labels for.");
            }

            setVerifiedItemsForLabels(labels);
            setShowLabelModal(true);
        } catch (err) {
            console.error("Barcode resolution error:", err);
            toast.error("Failed to fetch accurate barcode data from server.");
        }
    };

    const handlePrintInvoice = () => {
        if (!purchase) return;

        const doc = new jsPDF();
        const refNo = purchase.referenceNumber || purchase.purchase_id || purchase._id || "REF-UNKNOWN";

        doc.setFontSize(22);
        doc.setTextColor(105, 108, 255); // Brand Color
        doc.text("PURCHASE RECEIPT / GRN", 14, 22);

        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text(`Ref: ${refNo}`, 14, 30);
        doc.text(`Date: ${new Date().toLocaleDateString()}`, 14, 35);

        const sourceName = purchase.purchase_type === "Trade-In"
            ? `${purchase.customer?.first_name || 'Customer'} ${purchase.customer?.last_name || ''}`
            : (purchase.supplier?.business_name || purchase.supplier?.name || "Walk-in");

        doc.text(`Source: ${sourceName}`, 14, 40);

        const tableBody = summaryItems.map(item => {
            return [
                item.name,
                item.added, // Quantity added
                item.unit,
                'Verified'
            ];
        });

        autoTable(doc, {
            startY: 45,
            head: [['Item', 'Qty Added', 'Unit', 'Status']],
            body: tableBody,
            theme: 'grid',
            headStyles: { fillColor: [0, 28, 58] } // Brand Dark
        });

        // Footer
        const finalY = doc.lastAutoTable.finalY + 10;
        doc.setFontSize(10);
        doc.setTextColor(150);
        doc.text("Generated by iShopMaster Procurement Center", 14, finalY);

        doc.save(`GRN_${refNo}.pdf`);
        toast.success("Invoice downloading...");
    };

    const handleWhatsAppShare = () => {
        if (!purchase) return;
        const companyProfile = companies?.[0];
        const message = WhatsAppFormatter.generatePO(purchase, companyProfile);

        const contact = purchase.purchase_type === "Trade-In"
            ? purchase.customer?.phone_number
            : (purchase.supplier?.contact_info?.contact_number || purchase.supplier?.contact_number);

        sendWhatsApp(contact, message);
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered backdrop="static" keyboard={false}>
            <Modal.Body className="text-center p-5">
                <div className="mb-4 text-success">
                    <FaCheckCircle size={80} className="mb-3" />
                    <h2 className="fw-bold">Stock Updated Successfully!</h2>
                    <p className="text-muted">The inventory has been updated and is now available for sale in POS.</p>
                </div>

                <Card className="border-0 shadow-sm bg-light mb-4 text-start">
                    <Card.Body>
                        <h6 className="fw-bold mb-3"><FaBoxOpen className="me-2" />Stock Impact Summary</h6>
                        <Table hover responsive className="bg-white rounded table-borderless mb-0">
                            <thead className="border-bottom">
                                <tr className="text-muted text-uppercase small">
                                    <th>Item</th>
                                    <th className="text-center">Previous</th>
                                    <th className="text-center">Added</th>
                                    <th className="text-center">New Level</th>
                                </tr>
                            </thead>
                            <tbody>
                                {summaryItems.map((item, idx) => (
                                    <tr key={idx} className="align-middle">
                                        <td className="fw-bold text-dark">{item.name}</td>
                                        <td className="text-center text-muted">{item.oldStock}</td>
                                        <td className="text-center text-success fw-bold">+{item.added}</td>
                                        <td className="text-center">
                                            <Badge bg="primary" className="px-3 py-2" style={{ fontSize: '0.9rem' }}>
                                                {item.newStock} {item.unit}
                                            </Badge>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>

                <div className="d-flex justify-content-center gap-2 mt-4 flex-wrap">
                    <Button variant="outline-secondary" className="px-3" onClick={onHide}>
                        Close
                    </Button>
                    <Button
                        variant="outline-success"
                        className="px-3 d-flex align-items-center gap-2 fw-bold"
                        onClick={handleWhatsAppShare}
                    >
                        <FaWhatsapp size={18} /> WhatsApp
                    </Button>
                    <Button
                        variant="primary"
                        className="px-3 d-flex align-items-center gap-2"
                        onClick={handlePrepareLabels}
                    >
                        <FaBarcode /> Barcodes
                    </Button>
                    <Button
                        variant="dark"
                        className="px-3 d-flex align-items-center gap-2"
                        onClick={handlePrintInvoice}
                    >
                        <FaPrint /> Print
                    </Button>
                    <Button
                        variant="success"
                        className="px-4 shadow fw-bold d-flex align-items-center gap-2"
                        onClick={onMakePayment}
                    >
                        <FaMoneyBillWave /> Pay Now
                    </Button>
                </div>
            </Modal.Body>
            <Modal show={showLabelModal} onHide={() => setShowLabelModal(false)} size="xl" fullscreen>
                <Modal.Header closeButton>
                    <Modal.Title>Print Item Barcodes</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0">
                    <PrintableLabels items={verifiedItemsForLabels} />
                </Modal.Body>
            </Modal>
        </Modal>
    );
};

export default StockUpdateSuccessModal;
